import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, timer } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

/**
 * ⚡ OPTIMIZATION: Selective Preloading Strategy
 *
 * @description
 * Smart route preloading that respects priority levels and network conditions.
 * Routes can be configured with preload metadata to control loading behavior.
 *
 * @example
 * ```typescript
 * // In app.routes.ts
 * {
 *   path: 'patients',
 *   loadComponent: () => import('./patients/patient-list').then(m => m.PatientList),
 *   data: {
 *     preload: true,        // Enable preloading
 *     preloadPriority: 1,   // 1 = high priority (immediate), 2 = medium (2s delay), 3 = low (5s delay)
 *     preloadRoles: ['ADMIN', 'PHYSICIAN']  // Only preload for these roles
 *   }
 * }
 * ```
 *
 * @usageNotes
 * - Priority 1 (high): Preloaded immediately after app initialization
 * - Priority 2 (medium): Preloaded after 2s delay
 * - Priority 3 (low): Preloaded after 5s delay
 * - No priority: Defaults to medium (2s delay)
 * - Routes without `preload: true` are not preloaded
 * - Network condition checking (future enhancement)
 */
@Injectable({
  providedIn: 'root'
})
export class SelectivePreloadStrategy implements PreloadingStrategy {
  /**
   * Delay map for preload priorities
   */
  private readonly PRIORITY_DELAYS = {
    1: 0,      // High priority - immediate
    2: 2000,   // Medium priority - 2s delay
    3: 5000    // Low priority - 5s delay
  };

  /**
   * Determines if and when to preload a route
   * @param route The route to check
   * @param load Function to load the route
   * @returns Observable that triggers route loading
   */
  preload(route: Route, load: () => Observable<any>): Observable<any> {
    // Check if route should be preloaded
    if (!this.shouldPreload(route)) {
      return of(null);
    }

    // Get preload priority (default to medium if not specified)
    const priority = route.data?.['preloadPriority'] as 1 | 2 | 3 || 2;
    const delay = this.PRIORITY_DELAYS[priority];

    // Log preloading in development
    if (!this.isProduction()) {
      console.log(
        `⚡ Preloading route: ${route.path} (priority: ${priority}, delay: ${delay}ms)`
      );
    }

    // Preload after specified delay
    if (delay === 0) {
      return load();
    }

    return timer(delay).pipe(
      mergeMap(() => load())
    );
  }

  /**
   * Determines if a route should be preloaded
   * @param route The route to check
   * @returns True if route should be preloaded
   */
  private shouldPreload(route: Route): boolean {
    // Check if preload is explicitly enabled
    if (!route.data?.['preload']) {
      return false;
    }

    // Future enhancement: Check user role
    // const userRole = this.authService.getCurrentUserRole();
    // const allowedRoles = route.data?.['preloadRoles'];
    // if (allowedRoles && !allowedRoles.includes(userRole)) {
    //   return false;
    // }

    // Future enhancement: Check network conditions
    // const connection = (navigator as any).connection;
    // if (connection && connection.saveData) {
    //   return false; // Don't preload in data saver mode
    // }

    return true;
  }

  /**
   * Checks if running in production mode
   * @returns True if production
   */
  private isProduction(): boolean {
    return !('ngDevMode' in window && (window as any).ngDevMode);
  }
}
